// @ts-nocheck
import { EDU_CONFIG } from '../config';

const USER_DATA_KEY = 'EDUAPP_USER_DATA';
export const CACHE_TTL = 10 * 60 * 1000; // 10 minutes in milliseconds

/**
 * Extracts basic information from an item to save space in localStorage.
 */
export const extractBasicInfo = (item: any, resourceType: string): any => {
    if (!item) return null;

    // Default fields for most resources
    const basicFields: Record<string, string[]> = {
        classes: ['id', 'name', 'gradeLevel', 'academicYear', 'classType', 'enrollmentMode', 'inviteCode', 'studentIds', 'grade_level', 'academic_year', 'class_type', 'invite_code'],
        students: ['id', 'name', 'studentId', 'realName', 'dob', 'loginCode', 'student_id_code', 'real_name', 'parents'],
        homeworks: ['id', 'title', 'subject', 'gradeLevel', 'dueDate', 'status'],
        lessonPlans: ['id', 'title', 'subject', 'gradeLevel', 'savedDate'],
        quizzes: ['id', 'title', 'subject', 'gradeLevel', 'savedDate'],
        games: ['id', 'title', 'gameType', 'topic', 'savedDate'],
        presentations: ['id', 'title', 'topic', 'savedDate'],
        activities: ['id', 'title', 'activityType', 'savedDate'],
        documents: ['id', 'name', 'type', 'uploadDate', 'status'],
        videoProjects: ['id', 'name', 'createdAt'],
        videoResources: ['id', 'name', 'type', 'createdAt'],
        notifications: ['id', 'message', 'type', 'timestamp', 'isRead'],
        children: ['id', 'name', 'realName', 'dob', 'gender', 'studentId', 'avatarUrl', 'avatar', 'relationship'],
        skknProjects: ['id', 'title', 'subject', 'savedAt'],
        childLearningPlans: ['id', 'title', 'goal', 'difficulty', 'duration', 'planStatus', 'dailyPlans', 'daily_plans', 'childId', 'child_id'],
        learningHistory: ['id', 'planId', 'plan_id', 'moduleId', 'module_id', 'timestamp', 'isCorrect', 'is_correct']
    };

    const fields = basicFields[resourceType] || ['id', 'name', 'title'];
    const basicItem = {};
    const src = resourceType === 'skknProjects' && item.data ? { ...item, title: item.data?.title, subject: item.data?.subject } : item;

    fields.forEach(field => {
        if (src[field] !== undefined) {
            basicItem[field] = src[field];
        } else {
            // Hỗ trợ cả snake_case (phòng trường hợp backend trả về chưa chuẩn)
            const snakeField = field.replace(/[A-Z]/g, letter => `_${letter.toLowerCase()}`);
            if (src[snakeField] !== undefined) {
                basicItem[field] = src[snakeField];
            }
        }
    });

    return basicItem;
};

/**
 * Saves all user data to localStorage with basic info extraction.
 */
export const saveUserDataToLocal = (userId: string, data: Record<string, any[]>) => {
    if (!userId) return;
    
    const basicData = {
        timestamp: Date.now(),
        resources: {}
    };
    Object.keys(data).forEach(resourceType => {
        if (Array.isArray(data[resourceType])) {
            basicData.resources[resourceType] = data[resourceType].map(item => extractBasicInfo(item, resourceType));
        } else {
            basicData.resources[resourceType] = data[resourceType];
        }
    });

    try {
        localStorage.setItem(`${userId}_${USER_DATA_KEY}`, JSON.stringify(basicData));
    } catch (e) {
        console.error("Failed to save user data to localStorage", e);
    }
};

/**
 * Loads user data from localStorage with TTL check.
 */
export const loadUserDataFromLocal = (userId: string, ignoreTTL: boolean = false): Record<string, any[]> | null => {
    if (!userId) return null;
    
    try {
        const raw = localStorage.getItem(`${userId}_${USER_DATA_KEY}`);
        if (!raw) return null;
        
        const cached = JSON.parse(raw);
        
        // TTL Check
        if (!ignoreTTL && cached.timestamp && Date.now() - cached.timestamp > CACHE_TTL) {
            return null;
        }

        return cached.resources || null;
    } catch (e) {
        console.error("Failed to load user data from localStorage", e);
        return null;
    }
};

/**
 * Normalizes resource names to match keys used in storage.
 */
const normalizeResourceName = (name: string): string => {
    const mapping: Record<string, string> = {
        'lesson-plans': 'lessonPlans',
        'lesson_plans': 'lessonPlans',
        'quizzes': 'quizzes',
        'games': 'games',
        'presentations': 'presentations',
        'homeworks': 'homeworks',
        'activities': 'activities',
        'documents': 'documents',
        'video-resources': 'videoResources',
        'video_resources': 'videoResources',
        'video-projects': 'videoProjects',
        'video_projects': 'videoProjects',
        'notifications': 'notifications',
        'skkn-projects': 'skknProjects',
        'skkn_projects': 'skknProjects',
        'learning-plans': 'childLearningPlans',
        'learning_plans': 'childLearningPlans',
        'learning-history': 'learningHistory',
        'learning_history': 'learningHistory',
        'classes': 'classes',
        'students': 'students',
        'assignments': 'assignments',
        'submissions': 'submissions'
    };
    return mapping[name] || name;
};

/**
 * Updates a specific resource item in localStorage.
 */
export const updateLocalResource = (userId: string, resourceType: string, item: any, action: 'add' | 'update' | 'delete') => {
    if (!userId) return;
    
    const normalizedType = normalizeResourceName(resourceType);
    const resources = loadUserDataFromLocal(userId, true) || {};
    const list = resources[normalizedType] || [];
    
    let newList;
    const basicItem = action !== 'delete' ? extractBasicInfo(item, normalizedType) : null;

    if (action === 'add') {
        newList = [basicItem, ...list];
    } else if (action === 'update') {
        newList = list.map(i => String(i.id) === String(item.id) ? { ...i, ...basicItem } : i);
    } else if (action === 'delete') {
        const id = typeof item === 'object' ? item.id : item;
        newList = list.filter(i => String(i.id) !== String(id));
    }
    
    resources[normalizedType] = newList;
    saveUserDataToLocal(userId, resources);
};
